import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import { REVIEW, REVIEW_KEY } from '@/karehero-app/tell-us-once/intake/content';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

const SCREEN_PX = 3;

const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

const ReviewScreen = ({
  onComplete,
  gaps = false,
  initialEditing,
  initialExpanded,
  bar,
  account,
}: {
  onComplete?: () => void;
  /** Show the things still needing them. Off once nothing is missing. */
  gaps?: boolean;
  /** Harness-only: open one row for editing. See FactList's initialEditing. */
  initialEditing?: string;
  /** Harness-only: start with a group's count expanded. See FactList. */
  initialExpanded?: string;
  /** The calculator's app bar. Omit and the screen has none. */
  bar?: ReactNode;
  /** The account app bar. See StepScreen's own `account`. */
  account?: Account;
}) => {
  return (
    <ScreenRoot relative>
      <StatusBar />
      {bar}
      {account && <AccountBar {...account} />}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: SCREEN_PX,
          pb: '96px',
        }}
      >
        <Box sx={{ pt: PAGE_HEADER_TOP }}>
          <Stack spacing={3}>
            {/* h3, not h2. It was h2 while it carried the claim's name and
                had to hold the screen on its own; now the bar above names the
                claim and this only says which of its screens you are on. */}
            <PageHeader
              title={REVIEW.title}
              description={
                gaps ? REVIEW.descriptionWithGaps : REVIEW.description
              }
              variant='h3'
            />

            {/* Only when there are gaps. With none, the subheading says what
                the highlight means and a key would say it twice. */}
            {gaps && (
              <Stack spacing={0.75}>
                {REVIEW_KEY.map((entry) => (
                  <Stack
                    key={entry.label}
                    sx={{ alignItems: 'center' }}
                    direction='row'
                    spacing={1}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor:
                          entry.tone === 'ai'
                            ? 'custom.sunflower.500'
                            : 'custom.coral.500',
                      }}
                    />
                    <Typography
                      sx={{ color: 'text.secondary' }}
                      variant='caption'
                    >
                      {entry.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            <FactList
              groups={[...(gaps ? [REVIEW.gaps] : []), ...REVIEW.groups]}
              hideUnanswered={!gaps}
              initialEditing={initialEditing}
              initialExpanded={initialExpanded}
              // A row is changed in a drawer. Not a choice any more: a text
              // field in the row cannot serve a pick-several, which this form
              // is full of.
              edit='drawer'
            />
          </Stack>
        </Box>
      </Box>

      <Box
        sx={[
          {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            px: SCREEN_PX,
            py: 1.5,
          },
          frostedFooter,
        ]}
      >
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={REVIEW.primary}
          onPress={onComplete}
        >
          {REVIEW.primary}
        </Button>
      </Box>
    </ScreenRoot>
  );
};

export default ReviewScreen;
